<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useBreakpoints,
  breakpointsTailwind,
  useDebounceFn,
} from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useMultiSelectLabel } from 'dashboard/composables/useMultiSelectLabel';
import { avatarColor, initials } from '../agendaAvatar';
import { spDateKey } from '../agendaDateUtils';

const props = defineProps({
  activeView: { type: String, default: 'week' },
  currentDate: { type: Date, default: () => new Date() },
  activeFilters: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'update:activeView',
  'update:currentDate',
  'filterChange',
  'newMeeting',
]);

const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const VIEWS = [
  { key: 'list', label: t('AGENDA.VIEWS.LIST') },
  { key: 'day', label: t('AGENDA.VIEWS.DAY') },
  { key: 'threeDay', label: t('AGENDA.VIEWS.THREE_DAY') },
  { key: 'week', label: t('AGENDA.VIEWS.WEEK') },
  { key: 'month', label: t('AGENDA.VIEWS.MONTH') },
];

const MOBILE_ALLOWED_VIEWS = ['list', 'day'];

const visibleViews = computed(() =>
  isMobile.value
    ? VIEWS.filter(view => MOBILE_ALLOWED_VIEWS.includes(view.key))
    : VIEWS
);

const STATUS_OPTIONS = [
  {
    key: 'scheduled',
    label: t('AGENDA.STATUS.SCHEDULED'),
    color: 'text-n-blue-11',
    bgSelected: 'bg-n-blue-2',
    bgHover: 'hover:bg-n-blue-2',
  },
  {
    key: 'done',
    label: t('AGENDA.STATUS.DONE'),
    color: 'text-n-teal-11',
    bgSelected: 'bg-n-teal-2',
    bgHover: 'hover:bg-n-teal-2',
  },
  {
    key: 'no_show',
    label: t('AGENDA.STATUS.NO_SHOW'),
    color: 'text-n-ruby-11',
    bgSelected: 'bg-n-ruby-2',
    bgHover: 'hover:bg-n-ruby-2',
  },
  {
    key: 'rescheduled',
    label: t('AGENDA.STATUS.RESCHEDULED'),
    color: 'text-n-amber-11',
    bgSelected: 'bg-n-amber-2',
    bgHover: 'hover:bg-n-amber-2',
  },
  {
    key: 'cancelled',
    label: t('AGENDA.STATUS.CANCELLED'),
    color: 'text-n-slate-10',
    bgSelected: 'bg-n-alpha-2',
    bgHover: 'hover:bg-n-alpha-1',
  },
];

function initAssigneeIds(val) {
  if (Array.isArray(val)) return [...val];
  if (val) return [val];
  return [];
}

const filters = ref({
  assignee_id: initAssigneeIds(props.activeFilters.user_id),
  status: props.activeFilters.status ?? [],
});

const searchQuery = ref(props.activeFilters.search ?? '');

const INCLUDE_WATCHERS_STORAGE_KEY = 'agenda_include_watchers';
const includeWatchers = ref(
  localStorage.getItem(INCLUDE_WATCHERS_STORAGE_KEY) === 'true'
);

watch(includeWatchers, val => {
  localStorage.setItem(INCLUDE_WATCHERS_STORAGE_KEY, val ? 'true' : 'false');
});

const openDropdown = ref(null);
const dropdownAlign = ref('left');
const dropdownPos = ref({ top: 0, left: 0 });

function calcFlip(triggerEl, dropdownWidthPx) {
  if (!triggerEl) return 'left';
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  return rect.left + dropdownWidthPx > window.innerWidth - margin
    ? 'right'
    : 'left';
}

function calcPos(triggerEl, dropdownWidthPx, align) {
  if (!triggerEl) return { top: 0, left: 0 };
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  const top = rect.bottom + 4;
  const left =
    align === 'right'
      ? Math.max(margin, rect.right - dropdownWidthPx)
      : rect.left;
  return { top, left };
}

const assigneeSearch = ref('');
const statusSearch = ref('');
const assigneeHighlight = ref(0);
const statusHighlight = ref(0);

const ALL_LABEL = 'todos';

const filteredAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const filteredStatus = computed(() => {
  const q = statusSearch.value.toLowerCase();
  if (!q) return STATUS_OPTIONS;
  return STATUS_OPTIONS.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const assigneeNavigable = computed(() => {
  if (!assigneeSearch.value)
    return [{ id: '' }, ...(agents.value ?? []).map(a => ({ id: a.id }))];
  const items = filteredAgents.value.map(a => ({ id: a.id }));
  return ALL_LABEL.includes(assigneeSearch.value.toLowerCase())
    ? [{ id: '' }, ...items]
    : items;
});

const statusNavigable = computed(() => {
  if (!statusSearch.value)
    return [{ id: '' }, ...STATUS_OPTIONS.map(o => ({ id: o.key }))];
  const items = filteredStatus.value.map(o => ({ id: o.key }));
  return ALL_LABEL.includes(statusSearch.value.toLowerCase())
    ? [{ id: '' }, ...items]
    : items;
});

watch(assigneeNavigable, () => {
  assigneeHighlight.value = 0;
});
watch(assigneeSearch, () => {
  assigneeHighlight.value = 0;
});
watch(statusNavigable, () => {
  statusHighlight.value = 0;
});
watch(statusSearch, () => {
  statusHighlight.value = 0;
});

function isHighlighted(navigable, id, idx) {
  return navigable.findIndex(i => i.id === id) === idx;
}

const assigneeTriggerEl = ref(null);
const statusTriggerEl = ref(null);

const assigneeSelectedNames = computed(() =>
  filters.value.assignee_id.map(
    id => agents.value?.find(a => a.id === id)?.name ?? '…'
  )
);
const assigneeFitLabel = useMultiSelectLabel(
  assigneeTriggerEl,
  assigneeSelectedNames
);

const assigneeLabel = computed(
  () => assigneeFitLabel.value || t('AGENDA.FILTERS.ASSIGNEE')
);

const statusSelectedNames = computed(() =>
  filters.value.status.map(
    key => STATUS_OPTIONS.find(o => o.key === key)?.label ?? '…'
  )
);
const statusFitLabel = useMultiSelectLabel(
  statusTriggerEl,
  statusSelectedNames
);

const statusLabel = computed(
  () => statusFitLabel.value || t('AGENDA.FILTERS.STATUS')
);

function viewRange() {
  const d = props.currentDate;
  if (props.activeView === 'day') return { start: d, end: d };
  if (props.activeView === 'month') {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start, end };
  }
  if (props.activeView === 'threeDay') {
    const end = new Date(d);
    end.setDate(d.getDate() + 2);
    return { start: d, end };
  }
  // semana começa na segunda, igual à grade (AgendaWeekView)
  const start = new Date(d);
  const dayOfWeek = start.getDay();
  start.setDate(start.getDate() + (dayOfWeek === 0 ? -6 : 1 - dayOfWeek));
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

const showTodayButton = computed(() => {
  const { start, end } = viewRange();
  const todayKey = spDateKey(new Date());
  return !(spDateKey(start) <= todayKey && todayKey <= spDateKey(end));
});

function periodLabel() {
  const d = props.currentDate;
  const locale = 'pt-BR';
  if (props.activeView === 'day') {
    return d.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  if (props.activeView === 'month') {
    return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }
  const { start: rangeStart, end } = viewRange();
  const start = rangeStart.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
  const endStr = end.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
  return `${start} – ${endStr}`;
}

function navigate(direction) {
  const d = new Date(props.currentDate);
  const delta = direction === 'next' ? 1 : -1;
  if (props.activeView === 'week') d.setDate(d.getDate() + 7 * delta);
  else if (props.activeView === 'threeDay') d.setDate(d.getDate() + 3 * delta);
  else if (props.activeView === 'day') d.setDate(d.getDate() + delta);
  else if (props.activeView === 'month') {
    // dia 1 antes de trocar o mês: de 31/jan, setMonth(+1) cai em 3/mar
    d.setDate(1);
    d.setMonth(d.getMonth() + delta);
  }
  emit('update:currentDate', d);
}

function goToday() {
  emit('update:currentDate', new Date());
}

function applyFilters() {
  const active = {};
  if (filters.value.assignee_id.length) {
    active.user_id = filters.value.assignee_id;
    if (includeWatchers.value) active.include_watchers = true;
  }
  if (filters.value.status.length) active.status = filters.value.status;
  if (searchQuery.value) active.search = searchQuery.value;
  emit('filterChange', active);
}

// uma requisição por pausa na digitação, não por tecla
const onSearchInput = useDebounceFn(applyFilters, 300);

function setAssignee(id) {
  if (id === '') {
    filters.value.assignee_id = [];
    openDropdown.value = null;
  } else {
    const idx = filters.value.assignee_id.indexOf(id);
    if (idx === -1) filters.value.assignee_id.push(id);
    else filters.value.assignee_id.splice(idx, 1);
  }
  applyFilters();
}

function toggleIncludeWatchers() {
  includeWatchers.value = !includeWatchers.value;
  applyFilters();
}

function setStatus(key) {
  if (key === '') {
    filters.value.status = [];
    openDropdown.value = null;
  } else {
    const idx = filters.value.status.indexOf(key);
    if (idx === -1) filters.value.status.push(key);
    else filters.value.status.splice(idx, 1);
  }
  applyFilters();
}

const DROPDOWN_WIDTHS = {
  assignee: 192, // w-48
  status: 176, // w-44
};

function toggleDropdown(name) {
  const isOpening = openDropdown.value !== name;
  openDropdown.value = isOpening ? name : null;
  if (isOpening && DROPDOWN_WIDTHS[name]) {
    const triggerEl =
      name === 'assignee' ? assigneeTriggerEl.value : statusTriggerEl.value;
    dropdownAlign.value = calcFlip(triggerEl, DROPDOWN_WIDTHS[name]);
    dropdownPos.value = calcPos(
      triggerEl,
      DROPDOWN_WIDTHS[name],
      dropdownAlign.value
    );
  }
}

function makeKeydownHandler(
  dropdownName,
  getSearch,
  setSearch,
  getNavigable,
  getHighlight,
  setHighlight,
  selectFn
) {
  return function (e) {
    if (openDropdown.value !== dropdownName) return;
    if (e.key === 'Escape') {
      openDropdown.value = null;
      setSearch('');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(Math.min(getHighlight() + 1, getNavigable().length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(Math.max(getHighlight() - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = getNavigable()[getHighlight()];
      if (item) selectFn(item.id);
    } else if (e.key === 'Backspace') {
      setSearch(getSearch().slice(0, -1));
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      setSearch(getSearch() + e.key);
    }
  };
}

const onAssigneeKeydown = makeKeydownHandler(
  'assignee',
  () => assigneeSearch.value,
  v => {
    assigneeSearch.value = v;
  },
  () => assigneeNavigable.value,
  () => assigneeHighlight.value,
  v => {
    assigneeHighlight.value = v;
  },
  setAssignee
);

const onStatusKeydown = makeKeydownHandler(
  'status',
  () => statusSearch.value,
  v => {
    statusSearch.value = v;
  },
  () => statusNavigable.value,
  () => statusHighlight.value,
  v => {
    statusHighlight.value = v;
  },
  setStatus
);

const HANDLERS = { assignee: onAssigneeKeydown, status: onStatusKeydown };

watch(openDropdown, (val, old) => {
  if (old && HANDLERS[old])
    window.removeEventListener('keydown', HANDLERS[old]);
  if (val && HANDLERS[val]) window.addEventListener('keydown', HANDLERS[val]);
  if (old === 'assignee') assigneeSearch.value = '';
  if (old === 'status') statusSearch.value = '';
});

function onClickOutside(e) {
  if (
    !e.target.closest('[data-agenda-toolbar]') &&
    !e.target.closest('[data-agenda-toolbar-dropdown]')
  )
    openDropdown.value = null;
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => {
  Object.values(HANDLERS).forEach(h =>
    window.removeEventListener('keydown', h)
  );
  document.removeEventListener('mousedown', onClickOutside);
});

const hasActiveFilters = computed(
  () =>
    filters.value.assignee_id.length > 0 ||
    filters.value.status.length > 0 ||
    searchQuery.value.length > 0
);

function resetFilters() {
  filters.value = { assignee_id: [], status: [] };
  searchQuery.value = '';
  emit('filterChange', {});
}
</script>

<template>
  <div
    data-agenda-toolbar
    class="flex flex-wrap items-center gap-3 border-b border-n-weak px-3 md:px-6 py-2.5 flex-shrink-0 md:justify-between"
  >
    <!-- Esquerda: título + filtros -->
    <div class="flex items-center gap-3 flex-wrap">
      <span
        class="text-xl font-semibold text-n-slate-12 whitespace-nowrap flex-shrink-0"
      >
        {{ t('AGENDA.HEADER') }}
      </span>

      <!-- Filtro Responsável -->
      <div class="relative flex-shrink-0" @click.stop>
        <button
          ref="assigneeTriggerEl"
          class="flex items-center gap-1 text-sm border rounded-lg px-2.5 py-1.5 transition-colors focus:outline-none"
          :class="
            filters.assignee_id.length
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container'
          "
          @click="toggleDropdown('assignee')"
        >
          <span
            v-if="openDropdown === 'assignee' && assigneeSearch"
            class="text-n-slate-10"
            >{{ assigneeSearch }}</span
          >
          <span v-else>{{ assigneeLabel }}</span>
          <span class="i-lucide-chevron-down size-3.5 ml-0.5 text-n-slate-10" />
        </button>
      </div>

      <!-- Filtro Status -->
      <div class="relative flex-shrink-0" @click.stop>
        <button
          ref="statusTriggerEl"
          class="flex items-center gap-1 text-sm border rounded-lg px-2.5 py-1.5 transition-colors focus:outline-none"
          :class="
            filters.status.length
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background hover:border-n-container'
          "
          @click="toggleDropdown('status')"
        >
          <span
            v-if="openDropdown === 'status' && statusSearch"
            class="text-n-slate-10"
            >{{ statusSearch }}</span
          >
          <span v-else>{{ statusLabel }}</span>
          <span class="i-lucide-chevron-down size-3.5 ml-0.5 text-n-slate-10" />
        </button>
      </div>

      <!-- Reset -->
      <button
        v-if="hasActiveFilters"
        class="text-sm text-n-slate-10 hover:text-n-slate-12 px-1.5 py-1.5 flex-shrink-0"
        :title="t('AGENDA.FILTERS.RESET')"
        @click="resetFilters"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <div class="flex items-center gap-3 flex-wrap md:contents">
      <!-- Centro: navegação -->
      <div
        v-if="activeView !== 'list'"
        class="flex items-center gap-2 flex-shrink-0"
      >
        <button
          v-if="showTodayButton"
          class="text-sm border border-n-weak rounded-lg px-2.5 py-1 hover:bg-n-alpha-1 text-n-slate-11 whitespace-nowrap"
          @click="goToday"
        >
          {{ t('AGENDA.TOOLBAR.TODAY') }}
        </button>
        <button
          class="flex items-center justify-center size-7 border border-n-weak rounded-lg hover:bg-n-alpha-1 text-n-slate-11"
          @click="navigate('prev')"
        >
          <span class="i-lucide-chevron-left size-[18px] flex-shrink-0" />
        </button>
        <span
          class="text-sm font-medium text-n-slate-12 md:min-w-[140px] text-center whitespace-nowrap"
        >
          {{ periodLabel() }}
        </span>
        <button
          class="flex items-center justify-center size-7 border border-n-weak rounded-lg hover:bg-n-alpha-1 text-n-slate-11"
          @click="navigate('next')"
        >
          <span class="i-lucide-chevron-right size-[18px] flex-shrink-0" />
        </button>
      </div>

      <!-- Direita: busca + tabs de view + botão novo -->
      <div class="flex items-center gap-2 flex-shrink-0">
        <!-- ── Search ──────────────────────────────────────────────────── -->
        <div class="group relative flex items-center flex-shrink-0">
          <div
            class="flex items-center border rounded-lg overflow-hidden transition-all duration-200 group-hover:border-n-weak group-focus-within:border-n-brand"
            :class="searchQuery ? 'border-n-weak' : 'border-transparent'"
          >
            <input
              v-model="searchQuery"
              class="text-sm bg-transparent text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:outline-none focus:ring-0 transition-all duration-200 py-1.5"
              :class="
                searchQuery
                  ? 'w-44 px-2'
                  : 'w-0 px-0 group-hover:w-44 group-hover:px-2 group-focus-within:w-44 group-focus-within:px-2'
              "
              :placeholder="t('AGENDA.FILTERS.SEARCH_PLACEHOLDER')"
              @input="onSearchInput"
              @click.stop
            />
            <span
              class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0 mr-1.5 group-hover:text-n-slate-12 group-focus-within:text-n-brand transition-colors"
            />
          </div>
        </div>

        <div
          class="flex items-center gap-0.5 p-0.5 border border-n-weak rounded-lg flex-shrink-0"
        >
          <button
            v-for="view in visibleViews"
            :key="view.key"
            class="px-2.5 py-1 text-sm rounded-md transition-colors whitespace-nowrap"
            :class="
              activeView === view.key
                ? 'bg-n-alpha-2 text-n-brand font-medium'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="emit('update:activeView', view.key)"
          >
            {{ view.label }}
          </button>
        </div>
        <button
          class="flex items-center justify-center size-8 bg-n-brand text-white rounded-lg hover:brightness-110 flex-shrink-0"
          :title="t('AGENDA.NEW_MEETING')"
          @click="emit('newMeeting')"
        >
          <span class="i-lucide-plus size-4" />
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <!-- ── Dropdown: Responsável ── -->
    <div
      v-if="openDropdown === 'assignee'"
      data-agenda-toolbar-dropdown
      class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-48 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <button
        type="button"
        class="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors focus:outline-none border-b border-n-weak"
        @click="toggleIncludeWatchers"
      >
        <span>{{ t('AGENDA.FILTERS.INCLUDE_WATCHERS') }}</span>
        <span
          class="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors"
          :class="includeWatchers ? 'bg-n-teal-9' : 'bg-n-slate-6'"
        >
          <span
            class="inline-block size-3.5 transform rounded-full bg-white transition-transform"
            :class="includeWatchers ? 'translate-x-4' : 'translate-x-1'"
          />
        </span>
      </button>
      <template v-if="assigneeNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="
            isHighlighted(assigneeNavigable, '', assigneeHighlight)
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !filters.assignee_id.length
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
          "
          @click="setAssignee('')"
        >
          {{ t('AGENDA.FILTERS.ALL') }}
        </button>
      </template>
      <button
        v-for="agent in filteredAgents"
        :key="agent.id"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
        :class="
          isHighlighted(assigneeNavigable, agent.id, assigneeHighlight)
            ? 'text-n-brand font-medium bg-n-alpha-1'
            : filters.assignee_id.includes(agent.id)
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12'
        "
        @click="setAssignee(agent.id)"
      >
        <div
          class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
          :style="
            agent.thumbnail
              ? {
                  backgroundImage: `url(${agent.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : { backgroundColor: avatarColor(agent) }
          "
        >
          <span v-if="!agent.thumbnail">{{ initials(agent.name) }}</span>
        </div>
        <span class="truncate">{{ agent.name }}</span>
        <span
          v-if="filters.assignee_id.includes(agent.id)"
          class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
        />
      </button>
      <p
        v-if="assigneeSearch && !assigneeNavigable.length"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ t('AGENDA.FILTERS.NOTHING_FOUND') }}
      </p>
    </div>

    <!-- ── Dropdown: Status ── -->
    <div
      v-if="openDropdown === 'status'"
      data-agenda-toolbar-dropdown
      class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden py-1"
      :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }"
      @click.stop
    >
      <template v-if="statusNavigable.some(i => i.id === '')">
        <button
          class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
          :class="
            isHighlighted(statusNavigable, '', statusHighlight)
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : !filters.status.length
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
          "
          @click="setStatus('')"
        >
          {{ t('AGENDA.FILTERS.ALL') }}
        </button>
      </template>
      <button
        v-for="opt in filteredStatus"
        :key="opt.key"
        class="w-full flex items-center justify-between px-3 py-2 text-sm font-medium transition-colors focus:outline-none"
        :class="[
          opt.color,
          opt.bgHover,
          filters.status.includes(opt.key) ||
          isHighlighted(statusNavigable, opt.key, statusHighlight)
            ? opt.bgSelected
            : '',
        ]"
        @click="setStatus(opt.key)"
      >
        {{ opt.label }}
        <span
          v-if="filters.status.includes(opt.key)"
          class="i-lucide-check size-3 flex-shrink-0"
          :class="opt.color"
        />
      </button>
      <p
        v-if="statusSearch && !statusNavigable.length"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ t('AGENDA.FILTERS.NOTHING_FOUND') }}
      </p>
    </div>
  </Teleport>
</template>
